import React, { useState } from "react";
import { HashRouter, Routes, Route, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Phone, Mail, MapPin, ArrowRight, Landmark, Briefcase, DollarSign, PieChart, Crown, Wrench } from "lucide-react";

// -----------------------------------------
// Basic, self-contained UI (no shadcn needed)
// -----------------------------------------
const Btn = ({ as = "button", className = "", children, ...props }) => {
  const Cmp = as;
  const base =
    "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium border shadow-sm transition hover:shadow focus:outline-none focus:ring";
  return (
    <Cmp className={`${base} ${className}`} {...props}>
      {children}
    </Cmp>
  );
};

const Tag = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium bg-white shadow-sm ${className}`}>
    {children}
  </span>
);

const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl border bg-white shadow-sm ${className}`}>{children}</div>
);
const CardHeader = ({ children, className = "" }) => (
  <div className={`p-5 border-b ${className}`}>{children}</div>
);
const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>
);
const CardDescription = ({ children, className = "" }) => (
  <p className={`text-sm text-slate-600 ${className}`}>{children}</p>
);
const CardContent = ({ children, className = "" }) => (
  <div className={`p-5 ${className}`}>{children}</div>
);

// --- Brand constants ---
const COMPANY_NAME = "PID Co. - PID Investing & Development Company";
const COMPANY_URL = "https://PIDinvesting.com";
const TAGLINE = "Building Opportunity, Creating Value.";
const PRIMARY_PHONE = "(555) 123-4567";
const PRIMARY_EMAIL = "invest@pidinvesting.com";
const HQ_CITY = "Northern Virginia";

// ---------- Shared UI ----------
function Header() {
  const base = "px-2 py-1 rounded-md transition";
  const active = "text-emerald-700 font-semibold border-b-2 border-emerald-600";
  const idle = "hover:opacity-80";
  return (
    <header className="sticky top-0 z-50 backdrop-blur bg-white/80 border-b">
      <div className="mx-auto max-w-6xl px-4 flex h-16 items-center justify-between">
<Link to="/" className="flex items-center gap-2 font-semibold">
  <img src="/logo.png" alt="PID Co. logo" className="h-8 w-auto" />
  {COMPANY_NAME}
</Link>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <NavLink to="/portfolio" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>Real Estate Portfolio</NavLink>
          <NavLink to="/listing" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>Business for Sale</NavLink>
          <NavLink to="/contact" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>Contact</NavLink>
        </nav>
        <Btn as={Link} to="/contact" className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700">Discuss an Opportunity</Btn>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t bg-slate-50 py-6 text-sm text-slate-600">
      <div className="mx-auto max-w-6xl px-4 flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">{COMPANY_NAME}</a>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> {PRIMARY_PHONE}</span>
          <a className="flex items-center gap-1 hover:underline" href={`mailto:${PRIMARY_EMAIL}`}><Mail className="h-4 w-4" /> {PRIMARY_EMAIL}</a>
        </div>
<div>© 2025 PID Co., LLC. All rights reserved.</div>
      </div>
    </footer>
  );
}

// ---------- Media helpers ----------
function ListingImage() {
  // Multiple brewery-tank images to avoid single-CDN failures
  const urls = [
    "https://images.unsplash.com/photo-1514362545857-94f7493c1c38?auto=format&fit=crop&w=1600&q=70",
    "https://images.unsplash.com/photo-1603052875337-9a7d9f7e3e47?auto=format&fit=crop&w=1600&q=70",
    "https://images.unsplash.com/photo-1527167700-f9c547b4b2e8?auto=format&fit=crop&w=1600&q=70",
  ];
  const [idx, setIdx] = useState(0);
  const handleError = () => setIdx((i) => (i + 1) % urls.length);
  return (
    <img
      src={urls[idx]}
      onError={handleError}
      alt="Brewery tanks"
      className="rounded-2xl shadow-lg border mb-8 mx-auto object-cover h-64 w-full md:w-3/4"
    />
  );
}

// ---------- Pages ----------
function HomePage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <Header />
      {/* Hero */}
      <section className="relative w-full h-[80vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=60"
          alt="Modern city skyline"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white px-4 max-w-3xl">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-4xl md:text-6xl font-extrabold mb-4">
            Welcome to PID Co.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} className="text-lg md:text-xl mb-6">
            {TAGLINE} We specialize in strategic real estate and private business investments that generate lasting impact and compounding value.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <Btn className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" onClick={() => navigate("/listing")}>
              View Active Opportunity <ArrowRight className="ml-2 h-5 w-5" />
            </Btn>
          </motion.div>
        </div>
      </section>

      {/* Three-pillars */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-4 grid md:grid-cols-3 gap-8 text-center">
          <div>
            <Landmark className="mx-auto h-10 w-10 mb-3 text-emerald-700" />
            <h3 className="font-semibold text-lg">Real Estate Development</h3>
            <p className="text-sm text-slate-600 mt-2">We acquire, entitle, and develop residential and commercial properties across high-growth regions.</p>
          </div>
          <div>
            <Briefcase className="mx-auto h-10 w-10 mb-3 text-emerald-700" />
            <h3 className="font-semibold text-lg">Private Equity</h3>
            <p className="text-sm text-slate-600 mt-2">We buy and scale small-to-mid-sized companies with stable revenues and strong leadership teams.</p>
          </div>
          <div>
            <DollarSign className="mx-auto h-10 w-10 mb-3 text-emerald-700" />
            <h3 className="font-semibold text-lg">Value Creation</h3>
            <p className="text-sm text-slate-600 mt-2">Operational playbooks and growth strategies that drive measurable returns.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-700 to-emerald-500 text-white text-center">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Partner in Strategic Growth</h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            We combine disciplined capital allocation, operational expertise, and a long-term vision to create sustainable prosperity.
          </p>
          <Btn as={Link} to="/contact" className="bg-white text-emerald-700 border-white hover:bg-slate-50">Connect With Us <ArrowRight className="ml-2 h-5 w-5" /></Btn>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function PortfolioPage() {
  const projects = [
    {
      title: "The Arlington Collection",
      desc: "Luxury townhouse redevelopment with premium amenities and high ROI potential.",
      img: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=60",
    },
    {
      title: "Park City Retreat",
      desc: "Ski-in/ski-out mountain homes in Utah's premier alpine community.",
      img: "https://images.unsplash.com/photo-1501183638710-841dd1904471?auto=format&fit=crop&w=1200&q=60",
    },
    {
      title: "Clearwater Residences",
      desc: "Waterfront condo investment on Florida's Gulf Coast with strong rental demand.",
      img: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=60",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <section className="flex-grow py-20 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-6">Real Estate Portfolio</h1>
          <p className="text-slate-600 mb-12 max-w-3xl mx-auto">
            PID Co. invests in high-performing real estate projects across residential, commercial, and hospitality sectors.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {projects.map((p, i) => (
              <Card key={i} className="overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-48 object-cover" />
                <CardHeader>
                  <CardTitle>{p.title}</CardTitle>
                  <CardDescription>{p.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// ---------- Deal Structures (basic tabs) ----------
function ContactCTA({ label, interest }) {
  return (
    <Btn as={Link} to={`/contact?interest=${encodeURIComponent(interest)}`} className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700">{label}</Btn>
  );
}

function ComparisonStrip() {
  const Row = ({ name, equity, full, sweat }) => (
    <div className="grid grid-cols-4 gap-3 py-3 border-b last:border-b-0 even:bg-slate-50">
      <div className="text-left font-medium text-sm">{name}</div>
      <div className="text-sm"><span className="inline-flex items-center gap-1 text-emerald-700">{equity}</span></div>
      <div className="text-sm"><span className="inline-flex items-center gap-1 text-indigo-700">{full}</span></div>
      <div className="text-sm"><span className="inline-flex items-center gap-1 text-amber-700">{sweat}</span></div>
    </div>
  );
  return (
    <Card className="shadow-sm border-emerald-100">
      <CardHeader>
        <CardTitle>Quick Comparison</CardTitle>
        <CardDescription>Choose the path that fits your goals.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-3 text-center font-semibold text-sm mb-2">
          <div className="text-left" />
          <div className="flex items-center justify-center gap-2 rounded-full bg-emerald-50 px-3 py-1">
            <PieChart className="h-4 w-4 text-emerald-700" /> Equity Share
          </div>
          <div className="flex items-center justify-center gap-2 rounded-full bg-indigo-50 px-3 py-1">
            <Crown className="h-4 w-4 text-indigo-700" /> Full Purchase
          </div>
          <div className="flex items-center justify-center gap-2 rounded-full bg-amber-50 px-3 py-1">
            <Wrench className="h-4 w-4 text-amber-700" /> Sweat Equity
          </div>
        </div>
        <Row name="Ownership Outcome" equity="Pro-rata stake" full="100% control" sweat="Vesting by KPIs" />
        <Row name="Capital Required" equity="$$ investment" full="Purchase price" sweat="$0" />
        <Row name="Time Commitment" equity="Low" full="High (owner)" sweat="Full-time operator" />
        <Row name="Governance" equity="Minority protections" full="Board/owner control" sweat="Mgmt authority per plan" />
        <Row name="Economics" equity="Dividends/upsides" full="All cash flows" sweat="Rev share -> equity" />
        <Row name="Timeline" equity="30-60 days" full="45-90 days" sweat="2-4 weeks onboarding" />
      </CardContent>
    </Card>
  );
}

function DealStructures() {
  const [tab, setTab] = useState("equity");
  const TabBtn = ({ value, icon: Icon, label, color }) => (
    <button
      onClick={() => setTab(value)}
      className={`flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-sm border transition ${
        tab === value ? "bg-white shadow" : "bg-slate-100 hover:bg-slate-200"
      }`}
      aria-pressed={tab === value}
    >
      <Icon className={`h-4 w-4 ${color}`} />
      <span>{label}</span>
    </button>
  );

  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle>Deal Structures</CardTitle>
        <CardDescription>Three ways to partner on this brewery.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 bg-slate-100 rounded-xl p-1">
          <TabBtn value="equity" icon={PieChart} label="Equity Share" color="text-emerald-700" />
          <TabBtn value="full" icon={Crown} label="Full Purchase" color="text-indigo-700" />
          <TabBtn value="sweat" icon={Wrench} label="Sweat Equity" color="text-amber-700" />
        </div>

        {tab === "equity" && (
          <div className="mt-6 grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2 text-sm text-slate-600 leading-relaxed">
              <p className="mb-3"><span className="font-medium text-slate-900">What it is:</span> Minority or majority equity investment with aligned incentives.</p>
              <p className="mb-3"><span className="font-medium text-slate-900">Who it is for:</span> Investors seeking yield + upside with limited day-to-day involvement.</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Target raise: confidential (under NDA)</li>
                <li>Use: working capital, sales expansion, packaging scale</li>
                <li>Distributions: preferred return (if any) + pro-rata</li>
              </ul>
            </div>
            <div className="flex md:justify-end">
              <ContactCTA interest="Equity Share" label="Request CIM & Waterfall" />
            </div>
          </div>
        )}

        {tab === "full" && (
          <div className="mt-6 grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2 text-sm text-slate-600 leading-relaxed">
              <p className="mb-3"><span className="font-medium text-slate-900">What it is:</span> Acquisition of 100% equity (or substantially all assets).</p>
              <p className="mb-3"><span className="font-medium text-slate-900">Who it is for:</span> Strategic buyers or experienced operators seeking a turnkey platform.</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Deal: stock or asset; price upon request</li>
                <li>Transition: defined training and handover period</li>
                <li>Optional: consulting and transitional services</li>
              </ul>
            </div>
            <div className="flex md:justify-end">
              <ContactCTA interest="Full Purchase" label="Submit LOI Interest" />
            </div>
          </div>
        )}

        {tab === "sweat" && (
          <div className="mt-6 grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2 text-sm text-slate-600 leading-relaxed">
              <p className="mb-3"><span className="font-medium text-slate-900">What it is:</span> No-cash operator role with revenue share, converting into equity upon hitting milestones.</p>
              <p className="mb-3"><span className="font-medium text-slate-900">Who it is for:</span> Hands-on operator willing to drive growth and efficiency.</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Comp: revenue share and/or EBITDA-based bonus</li>
                <li>Milestones: distro footprint, monthly depletions, gross margin %, on-time production</li>
                <li>Vesting: equity grants at KPI gates (6/12/18 months)</li>
              </ul>
            </div>
            <div className="flex md:justify-end">
              <ContactCTA interest="Sweat Equity" label="Submit Operator Profile" />
            </div>
          </div>
        )}

        <div className="mt-8">
          <ComparisonStrip />
        </div>
      </CardContent>
    </Card>
  );
}

function ListingPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <section className="flex-grow bg-slate-50 py-20">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <Tag className="mb-3 bg-emerald-50 border-emerald-200 text-emerald-800">Active Listing</Tag>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Confidential Northern Virginia Brewery</h1>
          <p className="text-slate-600 max-w-2xl mx-auto mb-10">
            A profitable, distribution-focused craft brewery with established wholesale relationships and scalable operations.
          </p>
          <ListingImage />
          <p className="max-w-3xl mx-auto mb-10">
            This Northern Virginia brewery offers strong brand equity, an efficient production setup, and multiple growth paths through expanded distribution and portfolio diversification. Ideal for strategic buyers or experienced operators seeking a turnkey opportunity.
          </p>
          <DealStructures />
        </div>
      </section>
      <Footer />
    </div>
  );
}

function ContactPage() {
  const location = useLocation();
  const interest = new URLSearchParams(location.search).get("interest");
  const preset = interest ? `Interested in: ${interest}` : "";
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="flex-grow bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4">Start the Conversation</h1>
            <p className="text-slate-600 leading-relaxed mb-6">
              Whether you are exploring a sale, co-investment, or new development, we move with speed and discretion. Share a few details and our team will respond promptly.
            </p>
            {interest && (
              <Tag className="mb-4 bg-emerald-50 border-emerald-200 text-emerald-800">Interest: {interest}</Tag>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4" /> {PRIMARY_PHONE}</div>
              <a className="flex items-center gap-2 hover:underline" href={`mailto:${PRIMARY_EMAIL}`}><Mail className="h-4 w-4" /> {PRIMARY_EMAIL}</a>
            </div>
          </div>
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Confidential Inquiry</CardTitle>
              <CardDescription>We will follow up within one business day.</CardDescription>
            </CardHeader>
            <CardContent>
<form action="/contact.php" method="POST" className="grid gap-3">
  {/* Honeypot (hidden from users; bots will fill it) */}
  <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{position:"absolute", left:"-9999px"}} aria-hidden="true" />

  <input name="name" className="w-full rounded-xl border px-3 py-2 text-sm" placeholder="Full name" required />
  <input name="email" className="w-full rounded-xl border px-3 py-2 text-sm" placeholder="Email" type="email" required />
  <input name="company" className="w-full rounded-xl border px-3 py-2 text-sm" placeholder="Company (optional)" />
  <textarea name="message" className="w-full rounded-xl border px-3 py-2 text-sm min-h-[120px]" placeholder="How can we help?" defaultValue={preset} required />
  <button type="submit" className="w-full inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium border shadow-sm bg-emerald-600 text-white hover:bg-emerald-700">
    Send Inquiry
  </button>
  <p className="text-xs text-slate-500">By submitting, you agree to be contacted and to our standard NDA upon request.</p>
</form>

            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <section className="flex-grow flex items-center">
        <div className="mx-auto max-w-3xl px-4 text-center py-20">
          <h1 className="text-6xl font-extrabold mb-2">404</h1>
          <p className="text-slate-600 mb-6">We could not find that page. It may have moved or no longer exists.</p>
          <Btn as={Link} to="/" className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700">Return Home</Btn>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// ---------- App Router ----------
export default function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/listing" element={<ListingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </HashRouter>
  );
}
